import Util from '../util';
import ShopifyCart from '../shopify-cart';
import Translate from '../translate';
import SavedConfigs from '../config/saved-configs';

import * as log from 'loglevel';
import {CartItemProperties, RECOVER_SOURCE, ThemeCurrency} from '../const';

class Order {

    constructor(options) {
        // Default settings
        let defaults = {
            api: '',
            shop: '',
            app: ''
        };

        // Merge options into defaults
        this.settings = Util.extend(defaults, options || {});

        /**
         * @type {ShopifyCart}
         */
        this.shopifyCart = ShopifyCart.getInstance();
    }

    async restoreOrder(token){

        const loader = new LoadingOverlay({
            titleText: Translate.get('product.options.order.loading', null, 'Loading order...')
        });

        loader.show();

        try {
            // fetch all order items
            let order = await Order.fetchOrder(this.settings, token);

            // a single line again: keep only that item and add it next to what the cart holds
            let line = Util.getQueryParam(window.location.href, Order.LINE_PARAM);
            if(line){
                order.items = order.items.filter((item) => String(item.lineId) === line);
                if(!order.items.length){
                    log.debug('order line not found');
                    return;
                }
            } else {
                order.items.reverse(); // reverse to keep the same order of the items (last added first)

                // the whole order replaces the cart
                await this.shopifyCart.clear();
            }

            //  restore order items related infos (e.g file uploads) - can also edit the .items in the order object
            await this.restoreOrderItems(order);

            // mark order as restored
            let attributes = { from: 'LPO' };
            let sourceParam = Util.getQueryParam(window.location.href, Order.SOURCE_PARAM);
            if(sourceParam){
                attributes.from += '_' + sourceParam;
            }

            // add all items contained in the order
            const items = await this.shopifyCart.addItems(order.items, attributes);

            log.debug('restored cart items', items);

            if (items.length) {
                // remove all query params
                let url = new URL(window.location.href);
                url.search = '';
                window.location.href = url.toString();
            }

        } finally {
            loader.hide();
        }
    }

    // recovery link: the saved configurations of the token are appended to the cart with their stored line properties and quantity
    // marked from: LPO_recover like an order restore marks its cart - nothing to add or a failure only drops the parameter
    async restoreConfigs(token){

        const loader = new LoadingOverlay({ titleText: '' });

        loader.show();

        let items = [];
        let missing = [];

        try {
            let configs = await SavedConfigs.recovery(this.settings.api, this.settings.shop, token);

            if (Array.isArray(configs) && configs.length) {
                // INFO: no check against the cart - opened again, the configurations are added again ( a restored-token marker would block a line the shopper removed from ever coming back )

                let order = {
                    items: configs.map((config) => ({ id: config.variantId, quantity: config.quantity || 1, properties: Object.assign({}, config.properties) }))
                };

                //  restore related infos (e.g file uploads) - can also edit the .items in the order object
                await this.restoreOrderItems(order);

                let attributes = { from: 'LPO_' + Order.RECOVER_SOURCE };

                try {
                    items = await this.shopifyCart.addItems(order.items, attributes);
                } catch (error) {
                    // one line the cart refuses ( e.g. a deleted variant ) fails the whole add > line by line, the refused ones are named to the shopper
                    log.warn('recovery items not added together', error);
                    for (let i = 0; i < order.items.length; i++) {
                        try {
                            items = items.concat(await this.shopifyCart.addItems([order.items[i]], attributes));
                        } catch (itemError) {
                            missing.push(configs[i].productHandle || String(configs[i].variantId));
                        }
                    }
                }

                log.debug('recovered cart items', items);
            }
        } catch (error) {
            log.warn('recovery link not restored', error);
        } finally {
            loader.hide();
        }

        if (missing.length) {
            await SavedConfigs.standalone(this.settings, document.body).notice(
                SavedConfigs.t('recover.missing', { products: missing.join(', ') }, 'Some saved products could not be added to your cart: {products}')
            );
        }

        let url = new URL(window.location.href);
        url.searchParams.delete(SavedConfigs.recoverName);

        if (items.length) {
            // reload without the parameter to show the new lines
            window.location.href = url.toString();
        } else {
            window.history.replaceState(window.history.state, '', url.toString());
        }
    }

    // the order of a restore token with its items ( { id, name, items: [{ id, quantity, properties, lineId }] } ) - settings: api, shop, app
    static async fetchOrder(settings, token) {
        const response = await fetch(`${settings.api}/api/order/restore?token=${encodeURIComponent(token)}&shop=${settings.shop}&app=${settings.app}`);

        if (!response.ok) {
            throw new Error(`${response.status} ${response.statusText}`);
        }

        return response.json();
    }

    async restoreOrderItems(order){

        let urls = [];

        let exportUpdate = [];

        order.items.forEach((item) => {
            if(item.properties){
                for (let key in item.properties) {
                    if(item.properties.hasOwnProperty(key)){
                        let value = item.properties[key];
                        // check if contains a link to our app
                        if(Order.isFileUrl(value)){
                            // export extension ( /export or /transform link ) - register itemUpdate to overwrite the value with the restored URL that does NOT use the export anymore
                            if(Order.isExportUrl(value)){
                                exportUpdate.push((upload) => {
                                    // match based on source URL
                                    if(upload.meta?.exportUrl && value.includes(upload.meta.exportUrl)){
                                        // overwrite with new URL
                                        item.properties[key] = upload.url;
                                    }
                                });
                            }
                            urls.push(value);
                        }
                    }
                }

                // IMPORTANT: bundle add-on mode - recreate JSON for cart transform to preserve properties
                if(!ThemeCurrency.checkout.update && item.properties[CartItemProperties.OPTIONS]){
                    item.properties[CartItemProperties.JSON] =JSON.stringify(item.properties);
                }
            }
        });

        if(urls.length){
            let restoreFiles = await Order.restoreFiles(this.settings, urls);

            log.debug('restored uploads:', restoreFiles);

            // overwrite the export links with the restored URLs
            if(exportUpdate.length){
                restoreFiles.forEach((file) => {
                   exportUpdate.forEach((updateFn) => updateFn(file));
                });
            }
        }
    }

    // a link to a file of our app ( upload, export )
    static isFileUrl(value) {
        return typeof value === 'string' && value.includes('cloudlift.app') && value.startsWith('http');
    }

    // a rendered link ( classic /export, or /transform incl. the design form /transform/export ): the archive restores the rendered file to a new URL
    static isExportUrl(value) {
        return Order.isFileUrl(value) && (value.includes('/export/') || value.includes('/transform/'));
    }

    // checks all files to URLs and restores from archive if not found - settings: api, shop
    // children: the archived thumbnail and unedited original come along ( the product page shows them on the field )
    static async restoreFiles(settings, urls, children = false) {

        let response = await fetch(`${settings.api}/api/upload/restore?archive=1${children ? '&children=1' : ''}&shop=${settings.shop}`, {
            method: 'POST',
            headers: {
                'Content-Type': 'application/json'
            },
            body: JSON.stringify(urls)
        });

        return await response.json();
    }

}

Order.RESTORE_PARAM = 'cl_order';
Order.LINE_PARAM = 'cl_line';
Order.SOURCE_PARAM = 'source';
Order.RECOVER_SOURCE = RECOVER_SOURCE;

// util to create a loading overlay while restoring the order
class LoadingOverlay {
    constructor(options = {}) {
        this.options = {
            titleText: 'Loading...',
            ...options
        };
        this.createOverlay();
        this.setupStyles();
    }

    createOverlay() {
        this.overlay = document.createElement('div');
        this.overlay.className = 'cl-overlay';

        const container = document.createElement('div');
        container.className = 'cl-overlay-container';

        const title = document.createElement('div');
        title.className = 'cl-overlay-title';
        title.textContent = this.options.titleText;

        const spinner = document.createElement('div');
        spinner.className = 'cl-spinner';

        // Add elements to container
        container.appendChild(title);
        container.appendChild(spinner);
        this.overlay.appendChild(container);

    }

    setupStyles() {
        // Create a style element for our CSS
        const style = document.createElement('style');
        style.textContent = `
      .cl-overlay {
        position: fixed;
        top: 0;
        left: 0;
        width: 100%;
        height: 100%;
        background-color: rgba(0, 0, 0, 0.7);
        display: flex;
        justify-content: center;
        align-items: center;
        z-index: 999999;
        visibility: hidden;
        opacity: 0;
        transition: opacity 0.3s, visibility 0.3s;
      }

      .cl-overlay.active {
        visibility: visible;
        opacity: 1;
      }
      
      .cl-overlay-container {
        display: flex;
        flex-direction: column;
        align-items: center;
        gap: 2rem;
      }

      .cl-overlay-title {
        color: #fff;
        font-size: 2rem;
        font-weight: bold;
        text-align: center;
        margin: 0;
        text-shadow: 2px 2px 4px rgba(0, 0, 0, 0.3);
      }

      .cl-spinner {
        display:block !important;
        width: 50px;
        height: 50px;
        border: 5px solid #f3f3f3;
        border-top: 5px solid #787878;
        border-radius: 50%;
        animation: cl-spin 1s linear infinite;
      }

      @keyframes cl-spin {
        0% { transform: rotate(0deg); }
        100% { transform: rotate(360deg); }
      }
    `;

        // Add styles to document head
        document.head.appendChild(style);
    }

    show() {
        // Add to DOM if not already present
        if (!document.body.contains(this.overlay)) {
            document.body.appendChild(this.overlay);
        }

        // Use requestAnimationFrame to ensure DOM update before adding active class
        requestAnimationFrame(() => {
            this.overlay.classList.add('active');
        });
    }

    hide() {
        const handleTransitionEnd = () => {
            // Remove from DOM only after transition is complete
            if (document.body.contains(this.overlay)) {
                document.body.removeChild(this.overlay);
            }
            this.overlay.removeEventListener('transitionend', handleTransitionEnd);
        };

        this.overlay.addEventListener('transitionend', handleTransitionEnd);
        this.overlay.classList.remove('active');
    }
}

export default Order;